import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axios from '../../../api/axiosInstance';
import {
  DEFAULT_MENU_SECTIONS,
  MENU_ICON_OPTIONS,
  editableSections,
  flattenEditableMenuItems,
  getMenuIcon,
} from '../../../menu/menuConfig';

const defaultBranding = {
  appName: 'OBX App',
  subtitle: 'Web app',
  logoUrl: '',
};

function normalizeConfig(config = {}) {
  return {
    branding: { ...defaultBranding, ...(config.branding || {}) },
    menu: {
      sections: editableSections(config.menu || {}),
      items: flattenEditableMenuItems(config.menu || {}),
    },
  };
}

function compactPayload(config) {
  return {
    branding: config.branding,
    menu: {
      sections: config.menu.sections.map((section) => ({
        key: section.key,
        label: section.label,
        icon: section.icon,
        order: Number(section.order) || 0,
        visible: Boolean(section.visible),
        custom: Boolean(section.custom),
      })),
      items: config.menu.items.map((item) => ({
        key: item.key,
        text: item.text,
        icon: item.icon,
        sectionKey: item.parentKey ? undefined : item.sectionKey,
        order: Number(item.order) || 0,
        visible: Boolean(item.visible),
      })),
    },
  };
}

function IconSelect({ value, onChange, label = 'Icône' }) {
  return (
    <TextField select size="small" label={label} value={value || 'dashboard'} onChange={onChange} fullWidth>
      {MENU_ICON_OPTIONS.map((option) => (
        <MenuItem key={option.key} value={option.key}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ display: 'inline-flex', width: 22 }}>{getMenuIcon(option.key)}</Box>
            <span>{option.label}</span>
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
}

export default function GeneralSettingsPage() {
  const fileRef = useRef(null);
  const [config, setConfig] = useState(normalizeConfig());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const sectionOptions = useMemo(() => config.menu.sections, [config.menu.sections]);
  const parentLabels = useMemo(() => (
    config.menu.items.reduce((acc, item) => {
      if (!item.parentKey) acc[item.key] = item.text;
      return acc;
    }, {})
  ), [config.menu.items]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/settings/general');
      setConfig(normalizeConfig(data?.config || {}));
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger les paramètres généraux.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateBranding = (patch) => {
    setConfig((current) => ({ ...current, branding: { ...current.branding, ...patch } }));
  };

  const updateSection = (key, patch) => {
    setConfig((current) => ({
      ...current,
      menu: {
        ...current.menu,
        sections: current.menu.sections.map((section) => section.key === key ? { ...section, ...patch } : section),
      },
    }));
  };

  const addSection = () => {
    setConfig((current) => {
      const nextIndex = current.menu.sections.length + 1;
      const key = `custom_${Date.now()}`;
      return {
        ...current,
        menu: {
          ...current.menu,
          sections: [
            ...current.menu.sections,
            { key, label: `Nouvelle section ${nextIndex}`, icon: 'settings', order: nextIndex, visible: true, custom: true },
          ],
        },
      };
    });
  };

  const deleteSection = (key) => {
    setConfig((current) => ({
      ...current,
      menu: {
        sections: current.menu.sections.filter((section) => section.key !== key),
        items: current.menu.items.map((item) => item.sectionKey === key ? { ...item, sectionKey: '' } : item),
      },
    }));
  };

  const updateItem = (key, patch) => {
    setConfig((current) => ({
      ...current,
      menu: {
        ...current.menu,
        items: current.menu.items.map((item) => item.key === key ? { ...item, ...patch } : item),
      },
    }));
  };

  const resetDefaults = () => {
    setConfig(normalizeConfig({ branding: config.branding, menu: { sections: DEFAULT_MENU_SECTIONS, items: [] } }));
    setFeedback({ type: 'info', message: 'Menu réinitialisé localement. Enregistrez pour appliquer.' });
  };

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put('/api/settings/general', { config: compactPayload(config) });
      setConfig(normalizeConfig(data?.config || {}));
      setFeedback({ type: 'success', message: 'Paramètres généraux sauvegardés.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await axios.post('/api/settings/general/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateBranding({ logoUrl: data.logoUrl });
      setFeedback({ type: 'success', message: 'Logo envoyé. Enregistrez pour le conserver dans les paramètres.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Upload du logo impossible.' });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Chargement...</Typography>
        </Stack>
      </Paper>
    );
  }

  const logoSrc = config.branding.logoUrl ? `${process.env.REACT_APP_API_URL || ''}${config.branding.logoUrl}` : '/img/omnibox.png';

  return (
    <Stack spacing={2.5}>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Général</Typography>
            <Typography variant="body2" color="text.secondary">Nom, sous-titre et logo affichés dans la sidebar.</Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                size="small"
                label="Nom de l'application"
                value={config.branding.appName}
                onChange={(event) => updateBranding({ appName: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                size="small"
                label="Sous-titre"
                value={config.branding.subtitle}
                onChange={(event) => updateBranding({ subtitle: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Box component="img" src={logoSrc} alt="Logo" sx={{ maxWidth: 80, maxHeight: 46, objectFit: 'contain' }} />
                <Tooltip title="Uploader un logo">
                  <span>
                    <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()} disabled={uploading} startIcon={<UploadIcon />}>
                      Logo
                    </Button>
                  </span>
                </Tooltip>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Sections</Typography>
              <Typography variant="body2" color="text.secondary">Renommez, ordonnez et masquez les blocs du menu.</Typography>
            </Box>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}>Ajouter une section</Button>
          </Stack>
          <Stack spacing={1.25}>
            {config.menu.sections.map((section) => (
              <Grid container spacing={1.5} alignItems="center" key={section.key}>
                <Grid item xs={12} md={5}>
                  <TextField size="small" label="Nom" value={section.label} onChange={(event) => updateSection(section.key, { label: event.target.value })} fullWidth />
                </Grid>
                <Grid item xs={7} md={3}>
                  <IconSelect value={section.icon} onChange={(event) => updateSection(section.key, { icon: event.target.value })} />
                </Grid>
                <Grid item xs={5} md={2}>
                  <TextField size="small" type="number" label="Ordre" value={section.order} onChange={(event) => updateSection(section.key, { order: event.target.value })} fullWidth />
                </Grid>
                <Grid item xs={8} md={1.5}>
                  <FormControlLabel control={<Switch checked={Boolean(section.visible)} onChange={(event) => updateSection(section.key, { visible: event.target.checked })} />} label="Afficher" />
                </Grid>
                <Grid item xs={4} md={0.5} sx={{ textAlign: 'right' }}>
                  {section.custom && (
                    <Tooltip title="Supprimer la section">
                      <span>
                        <Button color="error" size="small" onClick={() => deleteSection(section.key)} sx={{ minWidth: 36, px: 1 }}>
                          <DeleteIcon fontSize="small" />
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Menu</Typography>
            <Typography variant="body2" color="text.secondary">Personnalisez les libellés, icônes, ordre et visibilité des entrées et sous-menus.</Typography>
          </Box>
          <Stack spacing={1.25} divider={<Divider flexItem />}>
            {config.menu.items.map((item) => (
              <Grid container spacing={1.5} alignItems="center" key={item.key}>
                <Grid item xs={12} md={3}>
                  <TextField size="small" label="Libellé" value={item.text} onChange={(event) => updateItem(item.key, { text: event.target.value })} fullWidth />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <IconSelect value={item.icon} onChange={(event) => updateItem(item.key, { icon: event.target.value })} />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  {item.parentKey ? (
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">Sous-menu</Typography>
                      <Chip size="small" label={parentLabels[item.parentKey] || item.parentKey} sx={{ alignSelf: 'flex-start' }} />
                    </Stack>
                  ) : (
                    <TextField select size="small" label="Section" value={item.sectionKey || ''} onChange={(event) => updateItem(item.key, { sectionKey: event.target.value })} fullWidth>
                      <MenuItem value="">Sans section</MenuItem>
                      {sectionOptions.map((section) => (
                        <MenuItem key={section.key} value={section.key}>{section.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <TextField size="small" type="number" label="Ordre" value={item.order} onChange={(event) => updateItem(item.key, { order: event.target.value })} fullWidth />
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <FormControlLabel control={<Switch checked={Boolean(item.visible)} onChange={(event) => updateItem(item.key, { visible: event.target.checked })} />} label="Afficher" />
                </Grid>
              </Grid>
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetDefaults}>Réinitialiser menu</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>{saving ? 'Sauvegarde...' : 'Enregistrer'}</Button>
      </Stack>
    </Stack>
  );
}
